import { uniqBy } from 'lodash'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { getAllBusinessDomains } from 'src/endpoints'
import { v4 } from 'uuid'

import { Col, Flex, Legend, Placeholder, Row } from '@/components/Layout'
import {
	Form,
	NamingInputFormField,
	SelectFormField,
	TextAreaFormField,
	TextFormField,
} from '@/components/UberForm'
import { FormlessFormField } from '@/components/UberForm/FormField'
import CheckboxFormField from '@/components/UberForm/Helpers/CheckBoxFormField'
import { useCanGenerateNaming } from '@/components/UberForm/Input/Naming/useCanGenerateNaming'
import { useApi } from '@/endpoints/hooks'
import { ObjectSettingsUpdateDto, StructureObjectDto } from '@/endpoints/models'
import {
	IdCodeName,
	PartitioningStrategy,
	TableData,
	TableOverview,
} from '@/endpoints/schemas'
import { PageType, TableMode } from '@/enums'
import {
	useAppContext,
	useAppDispatch,
	useCustomAttributesInitValues,
} from '@/hooks'
import { CustomAttributes } from '@/pages/User/pages/Home/components'
import ObjectSettingsControl from '@/pages/User/pages/Home/components/ObjectSettingsControl'
import {
	updateColumnsWithHistoryColumns,
	updateSequenceCode,
	updateStereotypeColumns,
} from '@/store/modules/table/actions'
import {
	defaultHistoryTableData,
	defaultReferenceTableData,
} from '@/store/modules/table/helpers'
import { callNamingWorkingData } from '@/store/modules/table/utils/callNaming'
import { MainContentWrap } from '@/styles'
import { NamingDtoTypeEnum, SqlTypeEnum, StereotypeTypeEnum } from '@/types'
import { formatCodeByCase } from '@/utils'
import { booleanString } from '@/utils/booleans'
import { useTechnologyId } from '@/utils/domain'
import { uniqueNameValidator } from '@/utils/validators'

import { FieldSetGap, FieldSetProject } from '../../../../components/FieldSet'
import {
	useResponsibleUsers,
	useStereotypes,
	useStructureStatus,
	useSystemUsers,
} from '../../../../utils'
import { SyncFormField } from '../../components/SyncFormField'
import {
	getPartitionStrategies,
	useEntityTypes,
	useRetentionTimes,
	useStrategies,
} from '../../lists'
import { SyncFormFieldNoPadding, TextFormFieldNoPadding } from '../../styles'
import {
	getIsFieldDisabled,
	getIsFieldDisabledGenerated,
	getNamingTypeCode,
	getSqlType,
} from '../../utils'
import {
	StatusCustomOption,
	StatusCustomSingleValue,
} from './StatusSelectOption'
import { OverviewProps } from './types'

export const Overview = ({
	node,
	data,
	systemNodeId,
	editMode,
	mode,
	onChange,
	unsyncedFields,
	onSync,
	modeForm,
	modeFormTable,
	selectedNodes,
	refForm,
}: OverviewProps) => {
	const { t } = useAppContext()
	const refInit = useRef(false)
	const dispatch = useAppDispatch()
	const technologyId = useTechnologyId(systemNodeId)
	const stereotypes = useStereotypes(StereotypeTypeEnum.TABLE)
	const businessDomains = useApi(getAllBusinessDomains())
	const userList = useResponsibleUsers(modeForm)
	const systemUsers = useSystemUsers(systemNodeId)

	const isHistoryMode = mode === TableMode.HISTORY

	const hasPartitioningColumn =
		modeForm.partitioningStrategy === PartitioningStrategy.KEY ||
		modeForm.partitioningStrategy === PartitioningStrategy.KEY_AND_SNAPSHOT ||
		modeForm.partitioningStrategy ===
			PartitioningStrategy.KEY_AND_SNAPSHOT_AND_DATE_EFFECTIVE

	const isKeepingFullStory =
		modeForm.retentionStrategy ===
			TableOverview.RetentionStrategyEnum.KEEP_FULL_HISTORY ||
		modeForm.retentionStrategy ===
			TableOverview.RetentionStrategyEnum.KEEP_FULL_HISTORY_AND_EOM

	const isKeepingFullStoryAndEOM =
		modeForm.retentionStrategy ===
		TableOverview.RetentionStrategyEnum.KEEP_FULL_HISTORY_AND_EOM

	const entityTypes = useEntityTypes(t)
	const nodeStates = useStructureStatus(t)
	const strategies = useStrategies(t)
	const retentionTimes = useRetentionTimes(t)
	const partitionStrategies = getPartitionStrategies(isHistoryMode, t)

	const { generateElementCode, isUpperCase } = useCanGenerateNaming(node)

	const {
		initialValuesCustomAttributes,
		getCustomAttributesReset,
		parseCustomAttribute,
		customAttributes,
	} = useCustomAttributesInitValues(
		data.form,
		StructureObjectDto.TypeEnum.TABLE,
		mode,
	)

	useEffect(() => {
		refInit.current = false
	}, [mode])

	// Memoize validators to prevent recreating debounced functions on every render
	const nameValidator = useMemo(
		() => uniqueNameValidator(dispatch, data.original.name, systemNodeId),
		[dispatch, data.original.name, systemNodeId],
	)
	const codeValidator = useMemo(
		() => uniqueNameValidator(dispatch, data.original.code, systemNodeId),
		[dispatch, data.original.code, systemNodeId],
	)

	const handleObjectSettingsChange = (newValue: IdCodeName[]) => {
		onChange({
			objectSettings: newValue,
		})
	}

	const handleChange = useCallback(
		async (item: Partial<TableData>, source?: FormlessFormField<TableData>) => {
			if (source) {
				const name = source.props.name

				if (name === 'code' && !refInit.current) {
					if (item.code) {
						refInit.current = true
					}

					return
				}

				refInit.current = true

				if (name === 'stereotypeId' && technologyId) {
					const stereotype = stereotypes.data?.find((s) => s.id === item[name])

					const { customAttributesForm, customAttributesRedux } =
						getCustomAttributesReset(stereotype?.id)

					refForm?.current?.setValues(customAttributesForm)

					onChange({
						stereotypeId: stereotype?.id,
						stereotypeCode: stereotype?.code,
						customAttributes: customAttributesRedux,
					})

					if (selectedNodes) {
						selectedNodes.forEach((node) =>
							dispatch(updateStereotypeColumns(node, technologyId, item[name])),
						)
					} else if (node) {
						dispatch(updateStereotypeColumns(node, technologyId, item[name]))
					}

					if (stereotype?.code === 'reference') {
						const sharedConfig = [
							Number(node.id),
							SqlTypeEnum.TABLE_REFERENCE,
							{
								master_node_name: modeForm.name,
								master_node_code: modeForm.code,
								body: { data: JSON.stringify({}) },
							},
						] as const

						const namings = await Promise.all([
							dispatch(
								callNamingWorkingData([
									NamingDtoTypeEnum.REFERENCE_TABLE_NAME,
									...sharedConfig,
								]),
							),
							dispatch(
								callNamingWorkingData([
									NamingDtoTypeEnum.REFERENCE_TABLE_CODE,
									...sharedConfig,
								]),
							),
						])

						onChange({
							hasReferenceTable: true,
							referenceTable: {
								uuid: v4(),
								name: namings[0]?.result,
								code: formatCodeByCase(
									namings[1]?.result,
									isUpperCase as boolean,
								),
							},
						})
					} else {
						onChange({
							hasReferenceTable: false,
							referenceTable: defaultReferenceTableData(),
						})
					}

					if (!stereotype?.children) {
						onChange({
							subStereotypeId: undefined,
						})
					}

					return
				}

				if (name === 'sequenceColumnCode') {
					if (selectedNodes) {
						selectedNodes.forEach((node) =>
							dispatch(
								updateSequenceCode(
									node,
									{ sequenceColumnCode: item.sequenceColumnCode },
									mode,
									[
										NamingDtoTypeEnum.SEQUENCE_NAME,
										node.id,
										{
											sequence_column: item.sequenceColumnCode,
										},
									],
								),
							),
						)
					} else if (node) {
						dispatch(
							updateSequenceCode(
								node,
								{ sequenceColumnCode: item.sequenceColumnCode },
								mode,
								[
									NamingDtoTypeEnum.SEQUENCE_NAME,
									node.id,
									{
										sequence_column: item.sequenceColumnCode,
									},
								],
							),
						)
					}

					onChange({
						[name]: item[name],
					})

					return
				}

				if (name === 'retentionStrategy') {
					if (item[name] === TableOverview.RetentionStrategyEnum.INFINITE) {
						onChange({
							retentionStrategyFullHistoryValue: undefined,
							retentionStrategyFullHistoryUnit: undefined,
							retentionStrategyEomValue: undefined,
							retentionStrategyEomUnit: undefined,
						})
					}

					if (
						item[name] === TableOverview.RetentionStrategyEnum.KEEP_FULL_HISTORY
					) {
						onChange({
							retentionStrategyFullHistoryValue: undefined,
							retentionStrategyFullHistoryUnit: undefined,
						})
					}

					onChange({
						[name]: item[name],
					})

					return
				}

				if (name === 'businessDomainId') {
					const businessDomain = businessDomains.data?.find(
						(d) => d.id === item[name],
					)

					onChange({
						businessDomainId: businessDomain?.id,
						businessDomainName: businessDomain?.name,
					})

					return
				}

				if (name === 'partitioningColumnId') {
					const column = modeForm.columns?.find((d) => d.id === item[name])

					onChange({
						partitioningColumnId: item[name],
						partitioningColumnCode: column?.code,
					})

					return
				}

				if (name === 'responsiblePersonId' || name === 'responsibleAnalystId') {
					const user = userList.data?.find((u) => u.id === item[name])

					const userName =
						name === 'responsiblePersonId'
							? 'responsiblePersonName'
							: 'responsibleAnalystName'

					onChange({
						[name]: user?.id,
						[userName]: user?.value,
					})

					return
				}

				if (name === 'hasHistoryTable' && technologyId) {
					if (selectedNodes) {
						selectedNodes.forEach((node) =>
							dispatch(
								updateColumnsWithHistoryColumns(
									node,
									technologyId,
									item.hasHistoryTable as boolean,
								),
							),
						)
					} else if (node) {
						dispatch(
							updateColumnsWithHistoryColumns(
								node,
								technologyId,
								item.hasHistoryTable as boolean,
							),
						)
					}

					if (item.hasHistoryTable) {
						// Always generate name/code on history table creation
						const sharedConfig = [
							Number(node.id),
							SqlTypeEnum.TABLE_HISTORY,
							{
								master_node_name: modeForm.name,
								master_node_code: modeForm.code,
								body: { data: JSON.stringify({}) },
							},
						] as const

						const namings = await Promise.all([
							dispatch(
								callNamingWorkingData([
									NamingDtoTypeEnum.HISTORICAL_TABLE_NAME,
									...sharedConfig,
								]),
							),
							dispatch(
								callNamingWorkingData([
									NamingDtoTypeEnum.HISTORICAL_TABLE_CODE,
									...sharedConfig,
								]),
							),
						])

						onChange({
							hasHistoryTable: item.hasHistoryTable,
							historyTable: {
								uuid: v4(),
								name: namings[0]?.result,
								code: formatCodeByCase(
									namings[1]?.result,
									isUpperCase as boolean,
								),
							},
						})
					} else {
						onChange({
							hasHistoryTable: item.hasHistoryTable,
							historyTable: defaultHistoryTableData(),
						})
					}

					return
				}

				if (name === 'ownerId') {
					const owner = systemUsers?.data?.find((u) => u.id === item[name])

					onChange({
						ownerId: owner?.id,
						ownerName: owner?.name,
					})

					return
				}

				const parsedCa = parseCustomAttribute(
					{
						[name]: item[name],
					},
					modeForm.customAttributes,
					customAttributes,
				)

				onChange(parsedCa)
			}
		},
		[
			technologyId,
			parseCustomAttribute,
			modeForm.customAttributes,
			modeForm.columns,
			customAttributes,
			onChange,
			stereotypes.data,
			getCustomAttributesReset,
			refForm,
			selectedNodes,
			node,
			dispatch,
			mode,
			modeForm.name,
			modeForm.code,
			isUpperCase,
			businessDomains.data,
			userList.data,
			systemUsers?.data,
		],
	)

	const namingConfig = useMemo(
		() =>
		node
			? ([
					getNamingTypeCode(mode),
					node.id,
					getSqlType(mode),
					{
						master_node_code: data.form.code,
						master_node_name: data.form.name,
						body: { data: JSON.stringify(modeForm) },
					},
				] as const)
			: undefined,
		[data.form.code, data.form.name, mode, modeForm.name, node],
	)

	return (
		<MainContentWrap>
			<Form<TableData>
				onChange={handleChange}
				disabled={!editMode}
				defaultValues={{ ...modeForm, ...initialValuesCustomAttributes }}
				key={`${booleanString(editMode)}-${mode}`}
				ref={refForm}
				enableFieldHighlight
			>
				<Row $alignItems="center">
					<Col size="medium">
						<TextFormField
							title={t('TABLE_GENERAL_NAME')}
							name="name"
							initialValue={modeForm.name}
							validators={[nameValidator]}
							required
							disabled={
								getIsFieldDisabledGenerated(
									'name',
									mode,
									generateElementCode,
								) || Boolean(selectedNodes)
							}
						/>
					</Col>
					<Col size="medium">
						<NamingInputFormField
							node={node}
							name="code"
							title={t('TABLE_GENERAL_CODE')}
							callNamingWorkingDataParams={namingConfig}
							disabled={
								getIsFieldDisabledGenerated(
									'code',
									mode,
									generateElementCode,
								) || Boolean(selectedNodes)
							}
							validators={[codeValidator]}
							initialValue={modeForm.code}
						/>
					</Col>
					<Col size="medium">
						<SelectFormField
							name="stereotypeId"
							title={t('TABLE_GENERAL_STEREOTYPE')}
							initialValue={modeForm.stereotypeId}
							fieldIsLoading={stereotypes.reloading}
							options={stereotypes.data || []}
							valueKey="id"
							labelKey="name"
							isNumeric
							allowEmpty
							isDisabled={getIsFieldDisabled('stereotypeId', mode, editMode)}
						/>
					</Col>
					<Col size="medium">
						<Placeholder $flexGrow={1}>
							<CheckboxFormField
								name="hasHistoryTable"
								title={t('TABLE_GENERAL_HAS_HISTORY_TABLE')}
								initialValue={modeForm.hasHistoryTable}
								isHidden={modeForm.hasReferenceTable || isHistoryMode}
							/>
						</Placeholder>
					</Col>
				</Row>
				<Row $alignItems="flex-start">
					<Col size="big">
						<SyncFormField
							unsyncedFields={unsyncedFields}
							disabled={!editMode}
							field="description"
							onSync={onSync}
						>
							<TextAreaFormField
								title={t('TABLE_GENERAL_DESCRIPTION')}
								name="description"
								initialValue={modeForm.description}
								rows={5}
							/>
						</SyncFormField>
					</Col>
					<Col size="big">
						<SyncFormField
							unsyncedFields={unsyncedFields}
							disabled={!editMode}
							field="comment"
							onSync={onSync}
						>
							<TextAreaFormField
								title={t('TABLE_GENERAL_COMMENT')}
								name="comment"
								initialValue={modeForm.comment}
								rows={5}
							/>
						</SyncFormField>
					</Col>
				</Row>
				<SyncFormField
					unsyncedFields={unsyncedFields}
					disabled={!editMode}
					field="objectSettings"
					onSync={onSync}
				>
					<ObjectSettingsControl
						name="objectSettings"
						label={t('OBJECTS')}
						onChange={handleObjectSettingsChange}
						disabled={!editMode}
						data={modeForm.objectSettings ?? []}
						type={ObjectSettingsUpdateDto.StructureTypeEnum.TABLE}
					/>
				</SyncFormField>
				<FieldSetProject>
					<Legend>{t('TABLE_GENERAL_PROJECT_METADATA')}</Legend>
					<Row>
						<Col size="medium">
							<SyncFormField
								unsyncedFields={unsyncedFields}
								field="status"
								onSync={onSync}
							>
								<SelectFormField
									name="status"
									title={t('TABLE_GENERAL_TABLE_STATUS')}
									initialValue={modeForm.status}
									options={nodeStates}
									valueKey="value"
									labelKey="label"
									allowEmpty
									isDisabled={!editMode}
									components={{
										SingleValue: StatusCustomSingleValue,
										Option: StatusCustomOption,
									}}
								/>
							</SyncFormField>
						</Col>
						<Col size="medium">
							<SyncFormField
								unsyncedFields={unsyncedFields}
								field="responsiblePersonId"
								onSync={onSync}
							>
								<SelectFormField
									name="responsiblePersonId"
									title={t('RESPONSIBLE_PERSON')}
									options={userList.data || []}
									valueKey="id"
									fieldIsLoading={userList.reloading}
									labelKey="value"
									isDisabled={!editMode}
									isNumeric
									allowEmpty
								/>
							</SyncFormField>
						</Col>
						<Col size="medium">
							<SyncFormField
								unsyncedFields={unsyncedFields}
								field="responsibleAnalystId"
								onSync={onSync}
							>
								<SelectFormField
									name="responsibleAnalystId"
									title={t('RESPONSIBLE_ANALYST')}
									options={userList.data || []}
									fieldIsLoading={userList.reloading}
									valueKey="id"
									labelKey="value"
									isDisabled={!editMode}
									initialValue={modeForm.responsibleAnalystId}
									isNumeric
									allowEmpty
								/>
							</SyncFormField>
						</Col>
					</Row>
				</FieldSetProject>
				<FieldSetGap>
					<Legend>{t('TABLE_GENERAL_BUSINESS_METADATA')}</Legend>
					<Row>
						<Col size="medium">
							<SyncFormField
								unsyncedFields={unsyncedFields}
								field="businessDomainId"
								onSync={onSync}
							>
								<SelectFormField
									name="businessDomainId"
									title={t('TABLE_GENERAL_BUSINESS_DOMAIN')}
									initialValue={modeForm.businessDomainId}
									options={businessDomains.data}
									fieldIsLoading={businessDomains.reloading}
									valueKey="id"
									labelKey="name"
									isDisabled={!editMode}
									isNumeric
									allowEmpty
								/>
							</SyncFormField>
						</Col>
					</Row>
				</FieldSetGap>
				<FieldSetGap>
					<Legend>{t('TABLE_GENERAL_TECHNICAL_METADATA')}</Legend>
					<Row>
						<Col size="medium">
							<SyncFormField
								unsyncedFields={unsyncedFields}
								field="entityType"
								onSync={onSync}
							>
								<SelectFormField
									name="entityType"
									title={t('TABLE_GENERAL_ENTITY_TYPE')}
									initialValue={modeForm.entityType}
									options={entityTypes}
									valueKey="value"
									labelKey="label"
									allowEmpty
									isDisabled={!editMode}
								/>
							</SyncFormField>
						</Col>
						<Col size="medium">
							<SyncFormField
								unsyncedFields={unsyncedFields}
								field="partitioningStrategy"
								onSync={onSync}
							>
								<SelectFormField
									title={t('TABLE_PARTITIONING_STRATEGY')}
									name="partitioningStrategy"
									options={partitionStrategies}
									isDisabled={!editMode}
									initialValue={modeForm.partitioningStrategy}
									allowEmpty
								/>
							</SyncFormField>
						</Col>
						{hasPartitioningColumn ? (
							<Col size="medium">
								<SyncFormField
									unsyncedFields={unsyncedFields}
									disabled={!editMode}
									field="partitioningColumnId"
									onSync={onSync}
								>
									<SelectFormField
										title={t('TABLE_PARTITIONING_COLUMN')}
										name="partitioningColumnId"
										options={modeFormTable.columns?.filter(
											(c) =>
												!!c.name &&
												!!modeFormTable.constraints?.find(
													(x) => !!x?.columns?.find((y) => y.code === c.code),
												),
										)}
										initialValue={modeForm.partitioningColumnId}
										valueKey="id"
										labelKey="name"
										isNumeric
										allowEmpty
										isDisabled={Boolean(selectedNodes)}
									/>
								</SyncFormField>
							</Col>
						) : (
							<Placeholder $flexGrow={1} $flexShrink={1} />
						)}
						<Col size="medium">
							<SyncFormField
								unsyncedFields={unsyncedFields}
								field="sequenceColumnCode"
								onSync={onSync}
							>
								<SelectFormField
									name="sequenceColumnCode"
									title={t('TABLE_GENERAL_SEQUENCE_COLUMN')}
									options={uniqBy(
										modeFormTable.columns?.filter((c) => !!c?.name),
										(column) => column?.code,
									)}
									isDisabled={!editMode || Boolean(selectedNodes)}
									valueKey="code"
									labelKey="name"
									initialValue={modeForm.sequenceColumnCode}
									allowEmpty
									clearable
								/>
							</SyncFormField>
						</Col>
						<Col size="medium">
							<SyncFormField
								unsyncedFields={unsyncedFields}
								disabled={!editMode}
								field="sequenceCode"
								onSync={onSync}
							>
								<TextFormFieldNoPadding
									title={t('TABLE_GENERAL_SEQUENCE')}
									name="sequenceCode"
									type="text"
									initialValue={modeForm.sequenceCode}
									isReadOnly={true}
									disabled={getIsFieldDisabled('sequenceCode', mode)}
								/>
							</SyncFormField>
						</Col>
						<Col size="medium">
							<SyncFormField
								unsyncedFields={unsyncedFields}
								field="ownerId"
								onSync={onSync}
							>
								<SelectFormField
									name="ownerId"
									title={t('NODE_OWNER')}
									options={systemUsers.data || []}
									fieldIsLoading={systemUsers.loading}
									initialValue={modeForm.ownerId}
									valueKey="id"
									labelKey="name"
									isNumeric
									isDisabled={!editMode}
									allowEmpty
									clearable
								/>
							</SyncFormField>
						</Col>
						<Col size="medium">
							<SyncFormField
								unsyncedFields={unsyncedFields}
								field="retentionStrategy"
								onSync={onSync}
							>
								<SelectFormField
									name="retentionStrategy"
									title={t('TABLE_GENERAL_TABLE_RETENTION_STRATEGY')}
									initialValue={modeForm.retentionStrategy}
									options={strategies}
									valueKey="value"
									labelKey="label"
									isDisabled={!editMode}
									allowEmpty
								/>
							</SyncFormField>
						</Col>
						{isKeepingFullStory ? (
							<Col size="medium">
								<Flex>
									<SyncFormFieldNoPadding
										unsyncedFields={unsyncedFields}
										disabled={!editMode}
										field="retentionStrategyFullHistoryValue"
										onSync={onSync}
									>
										<TextFormFieldNoPadding
											title={t(
												'TABLE_RETENTION_STRATEGY_KEEP_FULL_HISTORY_LABEL',
											)}
											name="retentionStrategyFullHistoryValue"
											type="number"
											initialValue={modeForm.retentionStrategyFullHistoryValue}
											required
											disabled={getIsFieldDisabled(
												'retentionStrategyFullHistoryValue',
												mode,
												editMode,
											)}
										/>
									</SyncFormFieldNoPadding>
									<SyncFormField
										unsyncedFields={unsyncedFields}
										field="retentionStrategyFullHistoryUnit"
										onSync={onSync}
									>
										<SelectFormField
											name="retentionStrategyFullHistoryUnit"
											title=" "
											initialValue={modeForm.retentionStrategyFullHistoryUnit}
											options={retentionTimes}
											valueKey="value"
											labelKey="label"
											isDisabled={!editMode}
										/>
									</SyncFormField>
								</Flex>
							</Col>
						) : (
							<Placeholder />
						)}
						{isKeepingFullStoryAndEOM ? (
							<Col size="medium">
								<Flex>
									<SyncFormField
										unsyncedFields={unsyncedFields}
										disabled={!editMode}
										field="retentionStrategyEomValue"
										onSync={onSync}
									>
										<TextFormFieldNoPadding
											title={t(
												'TABLE_RETENTION_STRATEGY_KEEP_FULL_HISTORY_AND_EOM_LABEL',
											)}
											name="retentionStrategyEomValue"
											type="number"
											initialValue={modeForm.retentionStrategyEomValue}
											required
											disabled={getIsFieldDisabled(
												'retentionStrategyEomValue',
												mode,
												editMode,
											)}
										/>
									</SyncFormField>
									<SyncFormField
										unsyncedFields={unsyncedFields}
										field="retentionStrategyEomUnit"
										onSync={onSync}
									>
										<SelectFormField
											name="retentionStrategyEomUnit"
											title=" "
											initialValue={modeForm.retentionStrategyEomUnit}
											options={retentionTimes}
											valueKey="value"
											labelKey="label"
											isDisabled={!editMode}
										/>
									</SyncFormField>
								</Flex>
							</Col>
						) : (
							<Placeholder />
						)}
					</Row>
				</FieldSetGap>
				<CustomAttributes
					customAttributes={customAttributes}
					$pageType={PageType.OVERVIEW_PAGE}
				/>
			</Form>
		</MainContentWrap>
	)
}
